// Unified chat-history view shared by the per-bot "Chat History" tab
// (/chatbots/:id/history) and the org-wide "Tenant History" page
// (/history). Lists every visitor thread — bot conversations and
// live-agent sessions — split into Guests / Staff sub-tabs, and opens a
// merged transcript (bot messages + live-agent messages, chronological)
// in a dialog. Data comes from HistoryController; org-scoped + gated by
// the conversations.view permission server-side.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MessageSquare, Bot, Headphones, User, FileText, Users } from "lucide-react";

export interface HistoryThread {
  key: string;
  conversationId: string | null;
  liveSessionId: string | null;
  chatbotId: string;
  chatbotName: string;
  displayName: string | null;
  email: string | null;
  channel: string;
  group: "guest" | "staff";
  hasBotChat: boolean;
  hasLiveAgent: boolean;
  messageCount: number;
  status: string;
  startedAt: string | null;
  lastActivityAt: string | null;
}

import { aiSourceLabel, type AiExecutionMetadata } from "@/lib/ai-execution-metadata";

interface TimelineItem extends AiExecutionMetadata {
  source: "bot" | "live";
  role: string;
  content: string;
  senderName: string | null;
  internalNote: boolean;
  attachmentUrl: string | null;
  attachmentName: string | null;
  at: string | null;
}

interface ThreadDetail {
  conversationId: string | null;
  liveSessionId: string | null;
  chatbotId: string;
  chatbotName: string;
  displayName: string | null;
  email: string | null;
  channel: string;
  group: "guest" | "staff";
  status: string;
  startedAt: string | null;
  timeline: TimelineItem[];
}

export function HistoryView({ listKey, showBotName }: { listKey: string[]; showBotName: boolean }) {
  const [group, setGroup] = useState<"guest" | "staff">("guest");
  const [selected, setSelected] = useState<HistoryThread | null>(null);

  const { data: threads, isLoading } = useQuery<HistoryThread[]>({ queryKey: listKey });

  const guestCount = (threads ?? []).filter((t) => t.group === "guest").length;
  const staffCount = (threads ?? []).filter((t) => t.group === "staff").length;
  const visible = (threads ?? []).filter((t) => t.group === group);

  return (
    <div className="space-y-4">
      <Tabs value={group} onValueChange={(v) => setGroup(v as "guest" | "staff")}>
        {/* 320px reflow: grid tabs — 1 column below 360px, 2 equal columns
            above; h-auto + flex-wrap keep label/badge visible, never clipped. */}
        <TabsList
          data-testid="tabs-history-group"
          className="grid h-auto w-full max-w-full grid-cols-1 gap-1 min-[360px]:grid-cols-2 sm:inline-flex sm:w-auto"
        >
          <TabsTrigger value="guest" data-testid="tab-history-guest" className="min-w-0 max-w-full flex-wrap whitespace-normal">
            <Users className="w-4 h-4 mr-1.5 shrink-0" /> Guests Chat
            <Badge variant="secondary" className="ml-2 text-[10px]">{guestCount}</Badge>
          </TabsTrigger>
          <TabsTrigger value="staff" data-testid="tab-history-staff" className="min-w-0 max-w-full flex-wrap whitespace-normal">
            <User className="w-4 h-4 mr-1.5 shrink-0" /> Staff Chat
            <Badge variant="secondary" className="ml-2 text-[10px]">{staffCount}</Badge>
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-5 w-48 mb-2" /><Skeleton className="h-4 w-32" /></CardContent></Card>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16" data-testid="empty-history">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <MessageSquare className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold mb-1">No {group === "guest" ? "guest" : "staff"} chats yet</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              {group === "guest"
                ? "Conversations from anonymous visitors will appear here."
                : "Conversations from signed-in members of your organization will appear here."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map((t) => (
            <button
              key={t.key}
              onClick={() => setSelected(t)}
              className="w-full text-left flex items-center justify-between gap-3 p-3 rounded-md bg-muted/40 hover:bg-muted/70 transition-colors"
              data-testid={`history-thread-${t.key}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-md bg-background border flex items-center justify-center flex-shrink-0">
                  {t.hasLiveAgent ? <Headphones className="w-4 h-4 text-muted-foreground" /> : <Bot className="w-4 h-4 text-muted-foreground" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate" data-testid={`text-thread-name-${t.key}`}>
                    {t.displayName || t.email || (t.group === "guest" ? "Anonymous visitor" : "Org member")}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {showBotName && <span>{t.chatbotName} · </span>}
                    {t.lastActivityAt ? new Date(t.lastActivityAt).toLocaleString() : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {t.hasBotChat && <Badge variant="outline" className="text-[10px] gap-1"><Bot className="w-3 h-3" />Bot</Badge>}
                {t.hasLiveAgent && <Badge variant="outline" className="text-[10px] gap-1"><Headphones className="w-3 h-3" />Live</Badge>}
                <Badge variant="secondary" className="text-[10px]">{t.messageCount} msg{t.messageCount !== 1 ? "s" : ""}</Badge>
              </div>
            </button>
          ))}
        </div>
      )}

      {selected && <TranscriptDialog thread={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function TranscriptDialog({ thread, onClose }: { thread: HistoryThread; onClose: () => void }) {
  const detailKey = thread.conversationId
    ? ["/api/history/conversations", thread.conversationId]
    : ["/api/history/live-sessions", thread.liveSessionId!];
  const { data: detail, isLoading } = useQuery<ThreadDetail>({ queryKey: detailKey });

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="dialog-transcript">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" /> Conversation History
          </DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-3"><Skeleton className="h-4 w-48" /><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>
        ) : detail ? (
          <div className="space-y-4">
            <div className="text-xs text-muted-foreground space-y-1">
              <p>Chatbot: {detail.chatbotName}</p>
              {detail.displayName && <p>Name: {detail.displayName}</p>}
              {detail.email && <p>Email: {detail.email}</p>}
              <p>Started: {detail.startedAt ? new Date(detail.startedAt).toLocaleString() : "N/A"}</p>
              <p className="flex items-center gap-1.5">
                <Badge variant="secondary" className="text-[10px]">{detail.group === "staff" ? "Staff" : "Guest"}</Badge>
                <Badge variant="outline" className="text-[10px]">{detail.channel}</Badge>
              </p>
            </div>
            <div className="space-y-2">
              {detail.timeline.length === 0 && <p className="text-sm text-muted-foreground">No messages in this conversation.</p>}
              {detail.timeline.map((m, i) => {
                const isVisitor = m.role === "user" || m.role === "visitor";
                const label = isVisitor
                  ? (detail.displayName || "Visitor")
                  : m.source === "live"
                    ? (m.senderName ? `${m.senderName} (agent)` : "Live agent")
                    : detail.chatbotName || "Bot";
                return (
                  <div
                    key={i}
                    className={`p-3 rounded-md text-sm ${isVisitor ? "bg-primary/10 ml-8" : m.internalNote ? "bg-amber-50 dark:bg-amber-950/30 mr-8" : "bg-muted/50 mr-8"}`}
                    data-testid={`transcript-message-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="text-xs font-medium flex items-center gap-1">
                        {m.source === "live" && <Headphones className="w-3 h-3" />}
                        {label}
                        {m.internalNote && <Badge variant="outline" className="text-[9px] ml-1">internal note</Badge>}
                      </span>
                      <span className="text-[10px] text-muted-foreground">{m.at ? new Date(m.at).toLocaleTimeString() : ""}</span>
                    </div>
                    <p className="whitespace-pre-wrap">{m.content}</p>
                    {m.role === "assistant" && aiSourceLabel(m) && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        <p>AI source: {aiSourceLabel(m)}</p>
                        {m.aiProvider && <p>Provider: {m.aiProvider} · Model: {m.aiModel || "Provider default"}</p>}
                      </div>
                    )}
                    {m.attachmentUrl && (
                      <a href={m.attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-xs underline mt-1 inline-block">
                        {m.attachmentName || "Attachment"}
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Unable to load this conversation.</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
